import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";

import cameras from "../data/cameras";
import parameters from "../data/parameters.json";
import parameterPages from "../data/parameter-pages.json";
import lightingConditions from "../data/lighting.json";
import scenarios from "../data/scenarios.json";
import movements from "../data/movement.json";
import looks from "../data/looks.json";
import curatedCombos from "../data/recipes-curated.json";

import { generateParameterPage, parameterApplies } from "../engine/parameterEngine";
import { usePublished } from "../utils/publishing";
import { coverageNote } from "../utils/cameraFacts";
import MeasurePrompt from "../components/MeasurePrompt";
import SplitPanel from "../components/SplitPanel";
import { CAMERA_NEUTRAL_GRADIENT } from "../utils/lightingGradients";
import { NdGear } from "../components/GearRecommendation";
import editorial from "../data/editorial.json";
import { setPageMeta, breadcrumbLd } from "../utils/meta";
import Footer from "../components/Footer";
import ShareButton from "../components/ShareButton";
import Faq from "../components/Faq";
import Icon from "../components/Icon";
import { SITE_LAUNCH_DATE } from "../config/site";




export default function ParameterPage() {
  const { cameraId, parameterId } = useParams();

  const camera = cameras.find((c) => c.id === cameraId);
  const parameter = parameters.find((p) => p.id === parameterId);

  // Same reasoning as RecipePageRoute: the route itself doesn't gate on
  // publishAt. These pages are prerendered, so gating here could only
  // remove content the CDN had already delivered. The edge function
  // decides what's served; the schedule below decides what's LINKED.
  const { items: livePages } = usePublished(parameterPages);
  const { items: liveCombos } = usePublished(curatedCombos);

  const entry = parameterPages.find((p) => p.cameraId === cameraId && p.parameterId === parameterId);

  const page = useMemo(() => {
    if (!camera || !parameter || !entry) return null;
    if (!parameterApplies(parameter, camera)) return null;
    return generateParameterPage(camera, parameter, {
      cameras,
      lightingConditions,
      scenarios,
      movements,
      looks,
    });
  }, [camera, parameter, entry]);

  // Sibling topics for this camera — the topic cluster. Only ones already
  // revealed, so the internal linking grows with the schedule.
  const siblings = useMemo(() => {
    if (!camera) return [];
    return livePages
      .filter((p) => p.cameraId === cameraId && p.parameterId !== parameterId)
      .map((p) => ({ ...p, def: parameters.find((d) => d.id === p.parameterId) }))
      .filter((p) => p.def);
  }, [livePages, camera, cameraId, parameterId]);

  // Human-written notes, keyed to this exact reference page or to the
  // camera as a whole. Rendered above everything derived.
  const humanNotes = useMemo(() => {
    const entry = editorial[`reference:${cameraId}/${parameterId}`] || editorial[`camera:${cameraId}`];
    return entry && entry.notes ? entry.notes : [];
  }, [cameraId, parameterId]);

  const recipesForCamera = useMemo(
    () => liveCombos.filter((c) => c.cameraId === cameraId).slice(0, 5),
    [liveCombos, cameraId]
  );

  useEffect(() => {
    if (!page || !camera) return;
    const path = `/cameras/${cameraId}/${parameterId}/`;
    setPageMeta({
      title: `${page.title}, Complete Reference`,
      description: page.metaDescription,
      path,
      type: "article",
      jsonLdId: "parameter-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            headline: page.title,
            description: page.metaDescription,
            about: { "@type": "Product", name: camera.name },
            url: `${window.location.origin}${path}`,
            datePublished: entry.publishAt || SITE_LAUNCH_DATE,
            dateModified: entry.publishAt || SITE_LAUNCH_DATE,
          },
          {
            "@type": "FAQPage",
            mainEntity: page.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: camera.name, path: `/cameras/${cameraId}/` },
            { name: parameter.name, path },
          ]),
        ],
      },
    });
  }, [page, camera, parameter, cameraId, parameterId]);

  if (!camera || !parameter || !page) {
    return (
      <main className="parameter-page">
        <h1>Reference not found</h1>
        <p>We don't have that topic for that camera.</p>
        <Link to="/">Back to all cameras</Link>
      </main>
    );
  }

  return (
    <>
    <SplitPanel
      gradient={CAMERA_NEUTRAL_GRADIENT}
      imageText={{ eyebrow: camera.name, title: parameter.name, sub: "Reference" }}
    >
    <article className="parameter-page split-article">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span>{" "}
        <Link to={`/cameras/${camera.id}`}>{camera.name}</Link> <span aria-hidden="true">/</span>{" "}
        <span>{parameter.name}</span>
      </nav>

      <header className="parameter-header">
        <div className="parameter-header-row">
          <h1>{page.title}</h1>
          {typeof window !== "undefined" && (
            <ShareButton
              title={page.title}
              text={page.summary}
              url={window.location.href}
              className="parameter-share-button"
            />
          )}
        </div>
        <p className="parameter-dek">{page.summary}</p>
      </header>

      {coverageNote(camera) && <p className="parameter-coverage">{coverageNote(camera)}</p>}

      {humanNotes.length > 0 && (
        <section className="depth-human" aria-labelledby="human-heading">
          <h2 id="human-heading">From using this camera</h2>
          {humanNotes.map((n, i) => (
            <div className="depth-block" key={i}>
              <h3>{n.title}</h3>
              <p>{n.text}</p>
            </div>
          ))}
        </section>
      )}

      {page.tables.map((table, i) => (
        <section key={i} aria-labelledby={`table-${i}`}>
          <h2 id={`table-${i}`}>{table.caption}</h2>
          <div className="table-scroll">
            <table className="parameter-table">
              <thead>
                <tr>{table.columns.map((c) => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {table.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) =>
                      c === 0 ? (
                        <th key={c} scope="row" className="data">{cell}</th>
                      ) : (
                        <td key={c} className={c === 1 ? "data" : ""}>{cell}</td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {parameterId === "nd-filter" && <MeasurePrompt camera={camera} />}
      {parameterId === "nd-filter" && camera.capabilities.ndSupport !== null && (
        <NdGear
          camera={camera}
          settings={{
            ndNeeded: true,
            ndStatus: camera.capabilities.ndSupport === false ? "recommended-unsupported" : "recommended",
            ndFilter: (camera.capabilities.ndStrengths || [])[Math.floor((camera.capabilities.ndStrengths || []).length / 2)] || "ND16",
            shutter: "1/48",
          }}
        />
      )}

      <section aria-labelledby="notes-heading">
        <h2 id="notes-heading">What this means in practice</h2>
        {page.notes.map((n, i) => <p key={i} className="parameter-note">{n}</p>)}
      </section>

      <section className="parameter-cta" aria-labelledby="cta-heading">
        <h2 id="cta-heading">Need this for a specific shot?</h2>
        <p>
          The table above is the camera-level answer. For a particular scenario and lighting
          condition, the builder runs the same engine with your inputs.
        </p>
        <Link to={`/builder?camera=${camera.id}`}>Open the builder for the {camera.name} &rarr;</Link>
      </section>

      {siblings.length > 0 && (
        <section aria-labelledby="siblings-heading">
          <h2 id="siblings-heading">More {camera.name} reference</h2>
          <ul className="parameter-links">
            {siblings.map((s) => (
              <li key={s.parameterId}>
                <Link to={`/cameras/${camera.id}/${s.parameterId}`}>
                  {camera.name} {s.def.name.toLowerCase()} settings
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {recipesForCamera.length > 0 && (
        <section aria-labelledby="recipes-heading">
          <h2 id="recipes-heading">{camera.name} shot recipes</h2>
          <ul className="parameter-links">
            {recipesForCamera.map((c) => {
              const scenario = scenarios.find((s) => s.id === c.scenarioId);
              const light = lightingConditions.find((l) => l.id === c.lightingId);
              return (
                <li key={c.slug}>
                  <Link to={`/camera/${c.slug}`}>{scenario?.name} in {light?.name}</Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <Faq items={page.faq} name="reference-faq" />

      <div data-prerender-ready="true" style={{ display: "none" }} />
    </article>
    </SplitPanel>
    <Footer />
    </>
  );
}
